import { useEffect, useRef, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  BookOpen,
  Bot,
  Check,
  ChevronRight,
  Headset,
  Lock,
  MessagesSquare,
  PlugZap,
  RefreshCw,
  UserPlus,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { homePath } from '@/components/shared/nav-config'
import { useAuthStore } from '@/stores/auth-store'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useMyPermissions } from '@/hooks/use-settings'
import { recordOnboardingStep, useDismissOnboarding, useOnboardingStatus } from '@/hooks/use-onboarding'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  FEATURED_CHANNELS,
  FEATURED_LABELS,
  ONBOARDING_LINKS,
  SHOW_INTEGRATION_GUIDE,
  computeSteps,
  isExternalUrl,
  markShownThisSession,
  nextPendingStep,
  progressPercent,
  quickActionAccess,
  readLastStep,
  rememberLastStep,
  stepFromHash,
  withOnboardingOrigin,
  type OnboardingStepNo,
  type StepState,
} from '@/lib/onboarding'
import { OAUTH_RESULT_PARAMS, parseOAuthResult } from '@/lib/oauth-result'
import {
  CHANNEL_BY_KEY,
  CHANNEL_TILE_CLASS,
  ChannelIcon,
  channelBandStyle,
  useChannelStats,
  type ChannelDef,
} from '@/pages/integrations/channel-catalog'
import { OnboardingChatIllustration } from './onboarding-chat-illustration'
import { OnboardingMobile } from './onboarding-mobile'
import { AiReplyIllustration, AutomationFlowIllustration, TeamInboxIllustration } from './onboarding-action-illustrations'

/**
 * Trang "Bắt đầu" (/onboarding) — theo bố cục onboarding của SaleSmartly:
 * cột chính (tiêu đề, tiến độ 0/2, 2 bước dọc, 3 việc giảm tải) + cột phải (lợi ích, gói, trợ giúp).
 * Tài khoản mới được đưa tới đây lần đầu (xem HomeRedirect); mở lại từ menu tài khoản → "Hướng dẫn bắt đầu".
 */
export function OnboardingPage() {
  const { t } = useTranslation('growth')
  const isMobile = useIsMobile()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: perms } = useMyPermissions()
  const status = useOnboardingStatus()
  const dismiss = useDismissOnboarding()
  const { stats, channels } = useChannelStats()

  const steps = computeSteps(status.data)
  const access = quickActionAccess(user?.role, perms)
  const canInbox = !perms || perms.has('conversations.view')
  const impersonating = !!(user?.impersonatedBy || user?.platformActorId)
  const location = useLocation()

  /** Rời trang để làm một bước: nhớ bước (quay lại cuộn đúng chỗ) + ghi phễu phía server. Link dùng push → Back về đây. */
  const openStep = (n: OnboardingStepNo) => {
    rememberLastStep(n)
    if (!impersonating) recordOnboardingStep(n)
  }
  const stepLink = (to: string, n: OnboardingStepNo) => withOnboardingOrigin(to, n)

  // Quay về từ OAuth (backend chuyển tới /onboarding?fb_connected=…): báo kết quả, dọn query, cuộn tới bước kế.
  const oauthHandled = useRef(false)
  useEffect(() => {
    if (oauthHandled.current) return
    const sp = new URLSearchParams(location.search)
    const r = parseOAuthResult(sp)
    if (!r) return
    oauthHandled.current = true
    for (const n of r.notices) toast[n.type](n.text)
    for (const k of OAUTH_RESULT_PARAMS) sp.delete(k)
    const q = sp.toString()
    // Kết nối xong → bước 2 (trả lời tin đầu); lỗi → ở lại bước 1 để thử lại.
    const next = r.ok ? 2 : 1
    rememberLastStep(next)
    navigate({ pathname: location.pathname, search: q ? `?${q}` : '', hash: `#step-${next}` }, { replace: true })
    void status.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ xử lý một lần khi vào trang
  }, [])

  // Cuộn tới `#step-n` (quay về từ trang đích / OAuth) hoặc bước mở gần nhất (Back trình duyệt) khi đã có dữ liệu.
  const loaded = !!status.data
  useEffect(() => {
    if (!loaded) return
    const n = stepFromHash(location.hash) ?? readLastStep()
    if (!n) return
    const id = window.requestAnimationFrame(() =>
      document.getElementById(`step-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
    return () => window.cancelAnimationFrame(id)
  }, [loaded, location.hash])

  const refresh = () => {
    void status.refetch()
    void qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
    void qc.invalidateQueries({ queryKey: ['widgets'] })
  }

  const skip = () => {
    const go = () => navigate(homePath(user?.role, perms), { replace: true })
    if (user) markShownThisSession(user.id)
    // Đã bỏ qua / hoàn thành trước đó, hoặc đang xem hộ → chỉ rời trang.
    if (impersonating || status.data?.dismissedAt || status.data?.completedAt) return go()
    dismiss.mutate(undefined, {
      onSuccess: go,
      onError: (err) => {
        toast.error(apiError(err))
        go()
      },
    })
  }

  const availableStep = (n: OnboardingStepNo) =>
    n === 1 ? access.connect : n === 2 ? canInbox : n === 3 ? access.ai : n === 4 ? access.automation : access.invite
  // Bước nên làm tiếp theo → làm nổi bật (khung + nhãn "Tiếp theo").
  const highlight = status.data ? nextPendingStep(status.data, null, availableStep) : null

  if (status.isLoading) return <Loading label={t('onboardingPage.loading')} className="h-[60vh]" />
  if (status.isError) return <ErrorState message={t('onboardingPage.loadError')} />

  // Điện thoại: bản rút gọn — các bước cần máy tính (kết nối kênh, AI, tự động hoá, mời người) thành "Gửi liên kết sang máy tính".
  if (isMobile) {
    return (
      <OnboardingMobile
        steps={steps}
        access={access}
        canInbox={canInbox}
        channelsConnected={status.data?.channelsConnected ?? 0}
        refreshing={status.isFetching}
        skipping={dismiss.isPending}
        onRefresh={refresh}
        onSkip={skip}
        onOpenStep={openStep}
      />
    )
  }

  const featured = FEATURED_CHANNELS.map((k) => CHANNEL_BY_KEY[k]).filter(Boolean)
  // "Nhiều kênh hơn": mỗi logo một lần (Zalo OA/Bot trùng logo Zalo cá nhân), kênh chưa ra mắt để sau.
  const seen = new Set(featured.map((c) => c.logo ?? c.key))
  const more = channels.filter((c) => {
    const id = c.logo ?? c.key
    if (seen.has(id) || (FEATURED_CHANNELS as readonly string[]).includes(c.key)) return false
    seen.add(id)
    return true
  })

  return (
    <div className="pb-6">
      <UpgradeBanner>
        <div className="flex items-center justify-end pt-2">
          <button
            type="button"
            onClick={skip}
            disabled={dismiss.isPending}
            className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
          >
            {t('onboardingPage.skipToSystem')} <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </UpgradeBanner>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── Cột chính ─────────────────────────────────────────── */}
        <div className="min-w-0 space-y-8">
          <section>
            <h1 className="max-w-3xl text-2xl font-semibold leading-snug tracking-tight md:text-[28px] md:leading-[1.3]">
              {t('onboardingPage.heroTitle')}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              {t('onboardingPage.heroDesc')}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-sm font-medium tabular-nums">
                {steps.doneCount}/{steps.total}
              </span>
              <div
                className="h-1.5 min-w-[160px] max-w-md flex-1 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label={t('onboardingPage.progressLabel')}
                aria-valuemin={0}
                aria-valuemax={steps.total}
                aria-valuenow={steps.doneCount}
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-[#4f7bff] transition-[width] duration-500"
                  style={{ width: `${progressPercent(steps)}%` }}
                />
              </div>
              <button
                type="button"
                onClick={refresh}
                disabled={status.isFetching}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-60"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', status.isFetching && 'animate-spin')} />
                {t('onboardingPage.refreshStatus')}
              </button>
            </div>
          </section>

          {/* ── Hai bước dọc ─────────────────────────────────────── */}
          <ol className="space-y-0">
            <Step
              index={1}
              highlight={highlight === 1}
              state={steps.connect}
              icon={PlugZap}
              title={t('onboardingPage.step1.title')}
              description={t('onboardingPage.step1.description')}
              last={false}
              aside={
                steps.connect === 'done' && access.connect ? (
                  <Button asChild variant="outline">
                    <Link to={stepLink('/integrations', 1)} onClick={() => openStep(1)}>
                      {t('onboardingPage.manageChannels')} <ArrowRight />
                    </Link>
                  </Button>
                ) : null
              }
            >
              <div className="mt-4 rounded-xl border bg-muted/30 p-3 sm:p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {featured.map((c) => (
                    <FeaturedChannel
                      key={c.key}
                      def={c}
                      connected={(stats[c.key]?.total ?? 0) > 0}
                      canConnect={access.connect}
                      onOpen={() => openStep(1)}
                    />
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t pt-4">
                  <span className="text-sm text-muted-foreground">{t('onboardingPage.moreChannels')}</span>
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    {more.map((c) => (
                      <span key={c.key} title={c.name} className={cn(c.comingSoon && 'opacity-60')}>
                        <ChannelIcon def={c} size="sm" />
                      </span>
                    ))}
                  </span>
                  <Button asChild variant="outline" size="sm">
                    <Link to={stepLink('/integrations', 1)} onClick={() => openStep(1)}>
                      {t('onboardingPage.viewAllChannels')}
                    </Link>
                  </Button>
                </div>
              </div>
            </Step>

            <Step
              index={2}
              highlight={highlight === 2}
              state={steps.reply}
              icon={MessagesSquare}
              title={t('onboardingPage.step2.title')}
              description={t('onboardingPage.step2.description')}
              last
              aside={
                steps.reply === 'locked' ? (
                  <Button variant="secondary" disabled>
                    <Lock /> {t('onboardingPage.waitingConnect')}
                  </Button>
                ) : canInbox ? (
                  <Button asChild variant={steps.reply === 'done' ? 'outline' : 'default'}>
                    <Link to={stepLink('/conversations', 2)} onClick={() => openStep(2)}>
                      {t('onboardingPage.openInbox')} <ArrowRight />
                    </Link>
                  </Button>
                ) : null
              }
            >
              <OnboardingChatIllustration state={steps.reply} />
            </Step>
          </ol>

          {/* ── Giảm tải ─────────────────────────────────────────── */}
          <section>
            <h2 className="text-lg font-semibold">{t('onboardingPage.offload.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('onboardingPage.offload.description')}
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <ActionCard
                step={3}
                highlight={highlight === 3}
                onOpen={openStep}
                icon={Bot}
                tone="violet"
                illustration={<AiReplyIllustration />}
                title={t('onboardingPage.card.ai.title')}
                description={t('onboardingPage.card.ai.description')}
                note={status.data?.aiConfigured ? t('onboardingPage.card.ai.note') : undefined}
                action={access.ai ? { label: t('onboardingPage.card.ai.action'), to: stepLink('/ai', 3) } : undefined}
              />
              <ActionCard
                step={4}
                highlight={highlight === 4}
                onOpen={openStep}
                icon={Workflow}
                tone="blue"
                illustration={<AutomationFlowIllustration />}
                title={t('onboardingPage.card.automation.title')}
                description={t('onboardingPage.card.automation.description')}
                note={status.data?.automationCount ? t('onboardingPage.card.automation.note', { count: status.data.automationCount }) : undefined}
                action={access.automation ? { label: t('onboardingPage.card.automation.action'), to: stepLink('/automation', 4) } : undefined}
              />
              <ActionCard
                step={5}
                highlight={highlight === 5}
                onOpen={openStep}
                icon={Users}
                tone="emerald"
                illustration={<TeamInboxIllustration />}
                title={t('onboardingPage.card.invite.title')}
                description={t('onboardingPage.card.invite.description')}
                note={(status.data?.membersCount ?? 0) > 1 ? t('onboardingPage.card.invite.note', { count: status.data?.membersCount }) : undefined}
                action={access.invite ? { label: t('onboardingPage.card.invite.action'), to: stepLink('/team/members?add=human', 5), icon: UserPlus } : undefined}
              />
            </div>
          </section>
        </div>

        {/* ── Cột phải ─────────────────────────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-[136px] lg:self-start">

          <SideCard className="space-y-1 p-2">
            {SHOW_INTEGRATION_GUIDE && (
              <HelpLink icon={BookOpen} label={t('onboardingPage.integrationGuide')} href={ONBOARDING_LINKS.integrationGuide} />
            )}
            <HelpLink icon={Headset} label={t('onboardingPage.contactAdvisor')} href={ONBOARDING_LINKS.productAdvisor} />
          </SideCard>
        </aside>
      </div>
    </div>
  )
}

function Step({
  index,
  highlight = false,
  state,
  icon: Icon,
  title,
  description,
  aside,
  last,
  children,
}: {
  index: number
  highlight?: boolean
  state: StepState
  icon: LucideIcon
  title: string
  description: string
  aside?: ReactNode
  last: boolean
  children?: ReactNode
}) {
  const { t } = useTranslation('growth')
  const done = state === 'done'
  return (
    <li id={`step-${index}`} className="relative flex scroll-mt-6 gap-4">
      {/* Đường nối dọc giữa các bước */}
      {!last && (
        <span
          aria-hidden
          className={cn('absolute left-[19px] top-11 bottom-1 w-px', state === 'done' ? 'bg-success/50' : 'bg-border')}
        />
      )}
      <span
        className={cn(
          'relative z-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-full border',
          state === 'done' && 'border-success/40 bg-success text-success-foreground',
          state === 'current' && 'border-primary/30 bg-primary/10 text-primary',
          state === 'locked' && 'bg-muted text-muted-foreground',
        )}
        aria-label={state === 'done' ? t('onboardingPage.stepDoneLabel', { index }) : t('onboardingPage.stepLabel', { index })}
      >
        {state === 'done' ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
      </span>
      <div
        className={cn(
          'min-w-0 flex-1',
          !last && 'mb-8',
          highlight && '-m-3 rounded-xl p-3 ring-2 ring-primary/40 transition-shadow',
          highlight && !last && 'mb-5',
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 basis-64">
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              {t('onboardingPage.stepLabel', { index })}
              {done && <span className="font-medium text-success">{t('onboardingPage.stepDoneSuffix')}</span>}
              {highlight && <NextBadge />}
            </p>
            <h3 className={cn('font-medium', state === 'locked' && 'text-muted-foreground')}>{title}</h3>
            {/* Bước đã xong → thu gọn: chỉ còn tiêu đề + nút (aside). */}
            {!done && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
          </div>
          {aside && <div className="shrink-0">{aside}</div>}
        </div>
        {!done && children}
      </div>
    </li>
  )
}

function FeaturedChannel({
  def,
  connected,
  canConnect,
  onOpen,
}: {
  def: ChannelDef
  connected: boolean
  canConnect: boolean
  onOpen: () => void
}) {
  const { t } = useTranslation('growth')
  const name = FEATURED_LABELS[def.key] ?? def.name
  return (
    <div className={cn(CHANNEL_TILE_CLASS, 'min-h-[104px] justify-between gap-3')} style={channelBandStyle(def)}>
      <span className="flex items-center gap-2.5">
        <ChannelIcon def={def} size="lg" />
        <span className="min-w-0 font-medium leading-snug">{name}</span>
      </span>
      <span className="flex items-center justify-between gap-2 text-sm">
        {connected ? (
          <span className="flex items-center gap-1 text-xs text-success">
            <Check className="h-3.5 w-3.5" /> {t('onboardingPage.connected')}
          </span>
        ) : (
          <span />
        )}
        {canConnect ? (
          <Link
            to={withOnboardingOrigin(`/integrations/${def.key}${connected ? '' : '?add=1'}`, 1)}
            onClick={onOpen}
            className="flex items-center gap-0.5 text-primary hover:underline"
          >
            {connected ? t('onboardingPage.manage') : t('onboardingPage.connectNow')} <ChevronRight className="h-4 w-4" />
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground" title={t('onboardingPage.needAddPermissionTitle')}>
            {t('onboardingPage.needManagePermission')}
          </span>
        )}
      </span>
    </div>
  )
}

const TONES = {
  blue: 'bg-primary/10 text-primary',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
} as const

function ActionCard({
  step,
  highlight = false,
  onOpen,
  icon: Icon,
  tone,
  title,
  description,
  note,
  action,
  illustration,
}: {
  step: OnboardingStepNo
  highlight?: boolean
  onOpen: (n: OnboardingStepNo) => void
  icon: LucideIcon
  tone: keyof typeof TONES
  title: string
  description: string
  note?: string
  action?: { label: string; to: string; icon?: LucideIcon }
  illustration?: ReactNode
}) {
  const { t } = useTranslation('growth')
  const ActionIcon = action?.icon
  return (
    <div
      id={`step-${step}`}
      className={cn('flex scroll-mt-6 flex-col rounded-xl border bg-card p-4', highlight && 'ring-2 ring-primary/40')}
    >
      {illustration && (
        <div className="relative mb-3">
          {illustration}
          {highlight && <span className="absolute right-2 top-2"><NextBadge /></span>}
        </div>
      )}
      {illustration ? (
        <h3 className="font-medium leading-snug">{title}</h3>
      ) : (
        <>
          <span className="flex items-start justify-between gap-2">
            <span className={cn('flex h-10 w-10 items-center justify-center rounded-lg', TONES[tone])}>
              <Icon className="h-5 w-5" />
            </span>
            {highlight && <NextBadge />}
          </span>
          <h3 className="mt-3 font-medium leading-snug">{title}</h3>
        </>
      )}
      <p className="mt-1 flex-1 text-sm leading-6 text-muted-foreground">{description}</p>
      {note && (
        <p className="mt-2 flex items-center gap-1 text-xs text-success">
          <Check className="h-3.5 w-3.5" /> {note}
        </p>
      )}
      {action ? (
        <Button asChild variant="outline" className="mt-4 self-start">
          <Link to={action.to} onClick={() => onOpen(step)}>
            {ActionIcon && <ActionIcon />} {action.label}
          </Link>
        </Button>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">{t('onboardingPage.contactAdminToSetup')}</p>
      )}
    </div>
  )
}

function NextBadge() {
  const { t } = useTranslation('growth')
  return (
    <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium leading-4 text-primary-foreground">
      {t('onboardingPage.next')}
    </span>
  )
}

/** Dải mời nâng gói ở đầu trang: gradient nổi bật, quyền lợi gọn trên một dòng, nút CTA trắng. */
function UpgradeBanner({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('growth')
  return (
    // Ghim khi cuộn: phủ nền trang phía sau để nội dung trượt bên dưới không lộ qua.
    <div className="sticky top-0 z-20 -mx-6 -mt-5 bg-background/95 px-6 pb-2 pt-5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-primary/15 bg-[linear-gradient(90deg,#eef3ff_0%,#f7f9ff_55%,#fff3e8_100%)] px-4 py-3 shadow-sm dark:bg-none dark:bg-card sm:px-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#ff9d57] to-[#f2701c] text-white shadow-sm shadow-[#f2701c]/30">
          <FatWalletIcon className="h-[22px] w-[22px]" />
        </span>
        <div className="min-w-0 flex-1 basis-72">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-semibold leading-snug">
            <span className="rounded-full bg-[#fff0e3] px-2 py-0.5 text-[11px] font-semibold text-[#d45a12] ring-1 ring-inset ring-[#ffd6b8] dark:bg-[#3a2414] dark:text-[#ffb27a] dark:ring-[#5a3519]">
              {t('onboardingPage.upgradeBanner.badge')}
            </span>
            {t('onboardingPage.upgradeBanner.title')}
          </p>
          <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
            {t('onboardingPage.upgradeBanner.description')}
          </p>
        </div>
        <Button asChild className="shrink-0">
          <Link to="/billing">
            {t('onboardingPage.upgradeBanner.cta')} <ArrowRight />
          </Link>
        </Button>
      </div>
      {children}
    </div>
  )
}

/** Biểu tượng "ví phồng": thân ví căng, tiền giấy nhô lên miệng ví, khoá cài bên phải. */
function FatWalletIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Tiền giấy nhô lên */}
      <path d="M6.5 7.5 15 4.3l1.3 3.2" />
      <path d="M10.5 7.5 18.6 5.6l.5 1.9" />
      {/* Thân ví phồng */}
      <path d="M4.6 7.5h14.3a2 2 0 0 1 2 2v7.3a2.7 2.7 0 0 1-2.7 2.7H6.4A3.4 3.4 0 0 1 3 16.1V9.1a1.6 1.6 0 0 1 1.6-1.6Z" />
      {/* Khoá cài */}
      <path d="M20.9 11h-4.1a2 2 0 0 0 0 4h4.1" />
      <circle cx="16.9" cy="13" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  )
}

function SideCard({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('rounded-xl border bg-card p-4', className)}>{children}</div>
}

function HelpLink({ icon: Icon, label, href }: { icon: LucideIcon; label: string; href: string }) {
  const { t } = useTranslation('growth')
  const cls =
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm transition-colors hover:bg-accent hover:text-primary'
  const inner = (
    <>
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </>
  )
  if (!href) {
    return (
      <button
        type="button"
        className={cls}
        onClick={() => toast.info(t('onboardingPage.advisorComingSoon'))}
      >
        {inner}
      </button>
    )
  }
  if (isExternalUrl(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    )
  }
  return (
    <Link to={href} className={cls}>
      {inner}
    </Link>
  )
}
